"use client";

import { useMemo } from "react";
import { OctagonMinus, Users } from "lucide-react";

import { MODELS } from "../constants";
import MultiSelect from "../MultiSelect";

interface AdvancedSettingsProps {
  selectedModel: string;
  is18Plus: boolean;
  isTextImage: boolean;
  isPublic: boolean;
  onModeChange: (type: string, value: boolean) => void;
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  selectedModel,
  is18Plus,
  isTextImage,
  isPublic,
  onModeChange,
}) => {
  const model = MODELS.find((m) => m.name === selectedModel) || MODELS[0];

  const selectedModes = useMemo(() => {
    const modes: { label: string; value: string }[] = [];

    if (is18Plus) {
      modes.push({
        label: "18+",
        value: "is18Plus",
      });
    }
    if (isPublic) {
      modes.push({
        label: "Public",
        value: "isPublic",
      });
    }

    if (isTextImage) {
      modes.push({
        label: "Text",
        value: "isTextImage",
      });
    }

    return modes;
  }, [is18Plus, isTextImage, isPublic]);

  const handleModeChange = (selections: string[]) => {
    const newModes = {
      is18Plus: selections.includes("is18Plus"),
      isTextImage: selections.includes("isTextImage"),
      isPublic: selections.includes("isPublic"),
    };

    if (newModes.is18Plus !== is18Plus) {
      onModeChange("is18Plus", newModes.is18Plus);
    }
    if (newModes.isTextImage !== isTextImage) {
      onModeChange("isTextImage", newModes.isTextImage);
    }
    if (newModes.isPublic !== isPublic) {
      onModeChange("isPublic", newModes.isPublic);
    }
  };

  return (
    <MultiSelect
      selected={selectedModes}
      options={[
        {
          label: "18+",
          value: "is18Plus",
          icon: <OctagonMinus size={14} />,
          disabled: !model.nsfw,
        },
        {
          label: "Public",
          value: "isPublic",
          icon: <Users size={14} />,
        },
        {
          label: "Text",
          value: "isTextImage",
          icon: (
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.75 10.5H1.75"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.91667 3.5H1.75"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.25 7H1.75"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
          disabled: !model.imageText,
        },
      ]}
      onChange={handleModeChange}
      label="Advanced Settings"
    />
  );
};

export default AdvancedSettings;
